import {useAuth} from 'app/modules/auth'
import {FC, useEffect, useMemo, useState} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import Button from 'app/components/Button'
import Searchbar from 'app/components/Searchbar'
import Select from './Select'
import {SingleValue} from 'react-select'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  orderBy,
  onSnapshot,
  serverTimestamp,
  query,
} from '@firebase/firestore'
import db, {storage} from 'services/firebase'
import {deleteObject, ref} from 'firebase/storage'
import {showSuccessToast} from 'app/components/ShowToast'
import AddEditSubprocessorModal from './modals/AddEditSubprocessorModal'
import Card from './Card'
import EditButton from './EditButton'
import ConfirmationModal from 'app/components/ConfirmationModal'
import CustomDrawer from './Drawer'

export type SubprocessorDataType = {
  id: string
  title: string
  logoURL: string | File
  logoName: string
  category: string
  hostingRegions: Array<string>
  logoId?: string
  isCustom?: boolean
}

type Props = {
  addedSubprocessors: SubprocessorDataType[]
}

const Subprocessors: FC<Props> = ({addedSubprocessors}) => {
  const {currentUser} = useAuth()

  const [defaultSubprocessors, setDefaultSubprocessors] =
    useState<{id: string; title: string; logoURL: string; category: string}[]>()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [toEditSubprocessor, setToEditSubprocessor] = useState<SubprocessorDataType>({
    id: '',
    title: '',
    category: '',
    logoName: '',
    logoURL: '',
    hostingRegions: [],
  })

  const [toDeleteSubprocessor, setToDeleteSubprocessor] = useState<SubprocessorDataType>()

  const [searchLength, setSearchLength] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredSubprocessors, setFilteredSubprocessors] =
    useState<SubprocessorDataType[]>(addedSubprocessors)
  const [selectedSubprocessorCategory, setSelectedSubprocessorCategory] = useState<{
    label: string
    value: string
  } | null>()

  const subprocessorsCategoryList = useMemo(() => {
    return Array.from(new Set(addedSubprocessors.map((subprocessor) => subprocessor.category)))
      .map((category) => ({label: category, value: category}))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [addedSubprocessors])

  useEffect(() => {
    try {
      const subprocessorsRef = collection(db, 'subprocessors')

      const queryRef = query(subprocessorsRef, orderBy('name'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const subprocessors = snapshot.docs.map((doc) => ({
          id: doc.id,
          title: doc.data().name as string,
          logoURL: doc.data().img_path as string,
          category: doc.data().category as string,
        }))

        const addedSubprocessorsTitleCategoryList = addedSubprocessors.map(
          (subprocessor) =>
            `${subprocessor.title.toLowerCase()}-${subprocessor.category.toLowerCase()}`
        )

        setDefaultSubprocessors(
          subprocessors.filter(
            (subprocessor) =>
              !addedSubprocessorsTitleCategoryList.includes(
                `${subprocessor.title.toLowerCase()}-${subprocessor.category.toLowerCase()}`
              )
          )
        )
      })

      return unsubscribe
    } catch (error) {
      console.error('Errow while getting default subprocessors for trust center', error)
    }
  }, [addedSubprocessors])

  useEffect(() => {
    let filteredData = addedSubprocessors.filter((subprocessor) =>
      subprocessor.title.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (selectedSubprocessorCategory?.value) {
      filteredData = filteredData.filter(
        (subprocessor) =>
          subprocessor.category.toLowerCase() === selectedSubprocessorCategory?.value.toLowerCase()
      )
    }

    setFilteredSubprocessors(filteredData)

    if (addedSubprocessors.length !== 0) {
      setSearchLength(filteredData.length === 0)
    }
  }, [searchTerm, selectedSubprocessorCategory, addedSubprocessors])

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  const handleRemoveSubprocessor = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const frameworksRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'subprocessors',
        'all_subprocessors'
      )

      if (toDeleteSubprocessor?.isCustom) {
        const fileRef = ref(
          storage,
          `tenants/${currentUser?.tenantId}/trust_center/subprocessors/${toDeleteSubprocessor.logoId}-${toDeleteSubprocessor?.logoName}`
        )

        await deleteObject(fileRef)
      }

      await deleteDoc(doc(frameworksRef, toDeleteSubprocessor?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeleteSubprocessor?.title} subprocessor for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Subprocessor removed',
      })

      showSuccessToast({message: 'Subprocessor has been removed successfully'})
    } catch (error) {
      console.error('Error while removing framework: ', error)
    } finally {
      setLoading(false)
      setToDeleteSubprocessor(undefined)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <div>
      <CustomDrawer isDrawerOpen={isDrawerOpen} onCancel={onCancel} title='Subprocessors'>
        <div style={{padding: '20px'}}>
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Add all subprocessors your company engages to handle critical data, including personal
            or customer information.
          </p>

          <div className='d-flex flex-column' style={{gap: '16px'}}>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1'>
              <span className='text-dark fs-6 fw-bold'>Subprocessors</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setToEditSubprocessor({
                    id: '',
                    title: '',
                    logoName: '',
                    logoURL: '',
                    category: '',
                    hostingRegions: [],
                  })
                  setIsModalOpen(true)
                }}
              >
                <img
                  alt='plus-icon'
                  src={toAbsoluteUrl('/media/icon/plus.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Add Subprocessor</span>
              </Button>
            </div>

            <div className='d-grid' style={{gridTemplateColumns: '65% auto', gap: '12px'}}>
              <div>
                <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} width='100%' />
              </div>

              <div>
                <Select
                  placeholder='Category'
                  options={subprocessorsCategoryList}
                  value={selectedSubprocessorCategory}
                  onChange={(value) => {
                    setSelectedSubprocessorCategory(
                      value as SingleValue<{label: string; value: string}>
                    )
                  }}
                />
              </div>
            </div>

            <div
              className='table-responsive'
              style={{
                borderRadius: '6.175px',
                border: '1px solid var(--bs-border-color)',
                maxHeight: '55vh',
                overflowY: 'auto',
              }}
            >
              <table className='table table-rounded gy-7 gs-7 mb-0 align-middle'>
                <thead>
                  <tr
                    className='fw-bold'
                    style={{
                      color: '#5A5A5A',
                      boxShadow: 'inset 0 -1px 0 var(--bs-border-color)',
                      backgroundColor: '#F9FAFB',
                      position: 'sticky',
                      top: 0,
                      zIndex: '10',
                    }}
                  >
                    <th className='min-w-100px'>SUBPROCESSOR TITLE</th>
                    <th className='min-w-100px'>CATEGORY</th>
                    <th className='min-w-50px text-end'>ACTIONS</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSubprocessors.length > 0 ? (
                    filteredSubprocessors.map((subprocessor, index) => (
                      <tr
                        style={{
                          borderBottom: `${
                            index !== filteredSubprocessors.length - 1
                              ? '1px solid var(--bs-border-color)'
                              : ''
                          }`,
                        }}
                        key={subprocessor.id}
                      >
                        <td>
                          <div className='d-flex align-items-center' style={{gap: '12px'}}>
                            <img
                              alt={`${subprocessor.title}-logo`}
                              src={toAbsoluteUrl(subprocessor.logoURL as string)}
                              className=''
                              style={{height: '32px', width: '32px'}}
                            />

                            <span className='flex-fill text-dark fw-medium fs-6'>
                              {subprocessor.title}
                            </span>
                          </div>
                        </td>

                        <td>
                          <span className='text-dark fs-6'>{subprocessor.category}</span>
                        </td>

                        <td>
                          <div className='d-flex justify-content-end' style={{gap: '8px'}}>
                            <Button
                              style={{
                                width: '34px',
                                height: '34px',
                              }}
                              onClick={() => {
                                setToEditSubprocessor(subprocessor)
                                setIsModalOpen(true)
                              }}
                            >
                              <img
                                alt='edit-icon'
                                src={toAbsoluteUrl('/media/icon/edit.svg')}
                                className=''
                                style={{height: '18px', width: '18px'}}
                              />
                            </Button>

                            <Button
                              style={{
                                width: '34px',
                                height: '34px',
                              }}
                              onClick={() => {
                                setIsDeleteModalOpen(true)
                                setToDeleteSubprocessor(subprocessor)
                              }}
                            >
                              <img
                                alt='trash-icon'
                                src={toAbsoluteUrl('/media/icon/trash.svg')}
                                className=''
                                style={{height: '18px', width: '18px'}}
                              />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3}>
                        <div
                          className='w-100 d-flex align-items-center justify-content-center'
                          style={{height: '245px'}}
                        >
                          {searchLength ? (
                            <div className='d-flex flex-column align-items-center gap-4'>
                              <img src='/media/buttons/emptystate.svg' alt='' />
                              <p className='mb-0' style={{fontWeight: '600'}}>
                                No subprocessors found
                              </p>
                              <p>
                                We couldn't find any subprocessors that match your search. Maybe try
                                a different search!
                              </p>
                            </div>
                          ) : (
                            <div
                              className='w-100 d-flex justify-content-center align-items-center'
                              style={{height: '100%'}}
                            >
                              <div className='d-flex flex-column align-items-center gap-4'>
                                <div
                                  className='d-flex align-items-center justify-content-center'
                                  style={{
                                    backgroundColor: '#F3F4F6',
                                    borderRadius: '12px',
                                    height: '48px',
                                    width: '48px',
                                  }}
                                >
                                  <img
                                    alt='stack-icon'
                                    src={toAbsoluteUrl('/media/icon/stack.svg')}
                                    className=''
                                    style={{height: '32px', width: '32px'}}
                                  />
                                </div>

                                <span className='text-dark fs-6 fw-medium'>
                                  No subprocessors added
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CustomDrawer>

      {isModalOpen && (
        <AddEditSubprocessorModal
          isModalOpen={isModalOpen}
          hideModal={() => setIsModalOpen(false)}
          isEditMode={Boolean(toEditSubprocessor.id)}
          subprocessor={toEditSubprocessor}
          availableSubprocessors={defaultSubprocessors!}
        />
      )}

      <Card
        title='Subprocessors'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        {addedSubprocessors.length > 0 ? (
          <div className='row gy-5 gx-10 me-0'>
            {addedSubprocessors.map((subprocessor, index) => (
              <div key={index} className='col-6 col-md-4 col-lg-3 pe-0'>
                <div
                  className='d-flex flex-column align-items-center'
                  style={{
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #F3F4F6',
                    borderRadius: '8px',
                    gap: '12px',
                    padding: '24px 16px',
                    height: '100%',
                  }}
                >
                  <img
                    alt={`${subprocessor.title}-logo`}
                    src={
                      typeof subprocessor.logoURL === 'string'
                        ? toAbsoluteUrl(subprocessor.logoURL)
                        : URL.createObjectURL(subprocessor.logoURL)
                    }
                    className='object-fit-cover'
                    style={{height: '48px', width: '48px'}}
                  />

                  <span className='fw-medium text-dark text-center fs-6'>{subprocessor.title}</span>

                  <span className='text-dark text-center' style={{fontSize: '13px'}}>
                    {subprocessor.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className='w-100 d-flex justify-content-center align-items-center'
            style={{height: '350px'}}
          >
            <div className='d-flex flex-column align-items-center gap-5'>
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '12px',
                  height: '48px',
                  width: '48px',
                }}
              >
                <img
                  alt='stack-icon'
                  src={toAbsoluteUrl('/media/icon/stack.svg')}
                  className=''
                  style={{height: '32px', width: '32px'}}
                />
              </div>

              <span className='text-dark fs-6 fw-medium'>No subprocessors added</span>
            </div>
          </div>
        )}
      </Card>

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => {
            setIsDeleteModalOpen(false)
          }}
          confirmBtnClickHandler={handleRemoveSubprocessor}
          toDeleteItemTitle='subprocessor'
          isLoading={loading}
        />
      )}
    </div>
  )
}

export default Subprocessors
